import { Zap, Droplets, Package } from 'lucide-react'
import type { Tea } from '@/entities/product'

export interface ProductTeaDetailsProps {
  tea: Tea
}

/**
 * Виджет: Детали чая (кофеин, окисление, хранение)
 */
export function ProductTeaDetails({ tea }: ProductTeaDetailsProps) {
  const hasCaffeine = !!tea.caffeineLevel
  const hasOxidation = tea.oxidationLevel != null && tea.oxidationLevel > 0
  const hasStorage = !!tea.storageTips?.trim()
  
  if (!hasCaffeine && !hasOxidation && !hasStorage) {
    return null
  }
  
  // Метки уровня кофеина
  const caffeineLabels: Record<string, { label: string; color: string; percent: number }> = {
    low: { label: 'Низкий', color: 'bg-green-500', percent: 25 },
    medium: { label: 'Средний', color: 'bg-amber-500', percent: 50 },
    high: { label: 'Высокий', color: 'bg-red-500', percent: 85 },
  }
  
  const caffeineInfo = tea.caffeineLevel ? caffeineLabels[tea.caffeineLevel] : null
  
  return (
    <div className="px-4 py-6 space-y-4">
      <h2 className="text-xl font-semibold text-ink dark:text-white">Характеристики</h2>
      
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Уровень кофеина */}
        {hasCaffeine && caffeineInfo && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Zap className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Кофеин</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-dark-border rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${caffeineInfo.color} rounded-full transition-all duration-500`}
                    style={{ width: `${caffeineInfo.percent}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-ink dark:text-white whitespace-nowrap">
                  {caffeineInfo.label}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Уровень окисления */}
        {hasOxidation && (
          <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Droplets className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Окисление</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-dark-border rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-600 rounded-full transition-all duration-500"
                    style={{ width: `${tea.oxidationLevel}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-ink dark:text-white whitespace-nowrap">
                  {tea.oxidationLevel}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Советы по хранению */}
      {hasStorage && (
        <div className="flex items-start gap-3 p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
          <Package className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Хранение</p>
            <p className="text-base text-ink dark:text-white leading-relaxed">
              {tea.storageTips}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

